import { Injectable, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { AuthService } from '@services/core/auth.service';
import { NotificationHubService } from '@services/core/notification-hub.service';
import { CatalogService } from '@services/data/catalog.service';
import { RequestService, Request } from '../../../feature/management-request/services/requests.service';
import { BpmService } from '../../../feature/management-request/services/bpm.service';
import { SocketService } from '@services/core/socket.service';

/** Pendiente de campana: solicitud + rol al que hay que cambiar al abrir. */
export interface PendingInboxItem {
	request: Request;
	targetRole: any;
}

@Injectable({ providedIn: 'root' })
export class AppNavService {
	private authService = inject(AuthService);
	private socketService = inject(SocketService);
	private requestService = inject(RequestService);
	readonly hub = inject(NotificationHubService);
	private catalogService = inject(CatalogService);
	private bpmService = inject(BpmService);
	private router = inject(Router);

	rolesList = computed(() => {
		const list = this.authService.sharingRoles() as any[];
		if (Array.isArray(list) && list.length) return list;
		const active = this.authService.sharingRole() as any;
		return active?.id != null ? [active] : [];
	});

	roleSelected = computed(() => this.authService.sharingRole() as any);

	private userMail = computed(() => {
		const identity = this.authService.sharingIdentity() as any;
		const data = this.authService.userData() as any;
		return String(identity?.mail ?? data?.mail ?? '').trim().toLowerCase() || null;
	});

	/**
	 * Pendientes de TODOS los roles asignados (da igual el activo).
	 * Cada ítem trae el rol destino para el click (cambiar rol → bandeja).
	 */
	pendingRequests = computed(() => {
		this.hub.openedKeys();
		const list = this.requestService.requests().filter(r => !this.bpmService.isClosed(r));
		const mail = this.userMail();
		const roles = this.rolesList();
		if (!roles.length) return [] as PendingInboxItem[];

		const byUuid = new Map<string, Request>();
		for (const role of roles) {
			const name = String(role?.name || '').toUpperCase();
			if (!name) continue;
			for (const item of this.bpmService.filterByRole(list, name, mail)) {
				const key = String(item.uuid || '');
				if (!key || this.hub.isOpened(item)) continue;
				byUuid.set(key, item);
			}
		}

		const out: PendingInboxItem[] = [];
		for (const req of byUuid.values()) {
			const targetRole =
				this.bpmService.resolveInboxRole(req, roles, mail) ||
				roles.find((r: any) =>
					this.bpmService.canActOnRequest(req, r?.name, mail)
				) ||
				roles[0];
			out.push({ request: req, targetRole });
			if (out.length >= 8) break;
		}
		return out;
	});

	recentNotices = computed(() => this.hub.items().slice(0, 8));

	badgeCount = computed(() => this.hub.unreadCount() + this.pendingRequests().length);

	rolesBadgeLabel(): string {
		const n = this.rolesList().length || 0;
		return n > 99 ? '99+' : String(n);
	}

	roleSelectedLabel(): string {
		const r = this.roleSelected();
		return r?.displayName || r?.description || r?.name || 'Cambiar rol';
	}

	isActiveRole(r: any): boolean {
		const active = this.roleSelected();
		return r?.id != null && active?.id != null && Number(r.id) === Number(active.id);
	}

	noticesBadgeLabel(): string {
		const n = this.badgeCount();
		return n > 99 ? '99+' : String(n);
	}

	changeRole(role: any): void {
		if (!role?.id) return;
		const targetId = Number(role.id);
		const identityRoleId = Number((this.authService.sharingIdentity() as any)?.roleId);
		const alreadySynced =
			this.isActiveRole(role) && Number.isFinite(identityRoleId) && identityRoleId === targetId;
		if (alreadySynced) return;

		this.authService.changeRole(targetId).subscribe({
			next: (res: any) => {
				const serverRole = res?.role || role;
				this.authService.applyActiveRole(serverRole);
				this.socketService.announceBrowserSession('context');
			},
			error: (err) => {
				const msg = err?.error?.error || err?.error?.message || err?.message || 'Intente nuevamente.';
				Swal.fire('No se pudo cambiar el rol', msg, 'error');
			}
		});
	}

	processName(item: PendingInboxItem): string {
		const req = item.request;
		return req.process?.name
			|| this.catalogService.processes().find(p => p.slug === req.processSlug)?.name
			|| req.processSlug;
	}

	processIcon(item: PendingInboxItem): string {
		const req = item.request;
		return req.process?.icon
			|| this.catalogService.processes().find(p => p.slug === req.processSlug)?.icon
			|| 'fa fa-clipboard-list';
	}

	partyLabel(item: PendingInboxItem): string {
		const req = item.request;
		const names = `${req.party?.names ?? ''} ${req.party?.lastNames ?? ''}`.trim();
		return names || (req.entityCode != null ? `Entidad ${req.entityCode}` : 'Solicitud');
	}

	pendingRoleLabel(item: PendingInboxItem): string {
		const r = item?.targetRole;
		return r?.displayName || r?.description || r?.name || item?.request?.statusObj?.name || 'Pendiente';
	}

	createdAt(item: PendingInboxItem): string | Date | undefined {
		return item?.request?.createdAt as any;
	}

	openPending(item: PendingInboxItem): void {
		void this.hub.openRequest(item.request.uuid, item.request, item.targetRole);
	}

	openInbox(): void {
		this.router.navigate(['/solicitudes']);
	}
}
